import React from 'react'

import { Plus, Download, XCircle, MapPin } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import addIcon from '@/assets/images/icons/add.png'
import filterIcon from '@/assets/images/icons/filter2.svg'
import { PaginatedResponse } from '@/core/api/dtos/common/types'
import { UpdateZoneRequestDTO } from '@/core/api/dtos/request/zone/update'
import { ZoneStateStatus } from '@/core/api/entities/zone/state'
import { Zone } from '@/core/api/entities/zone/zone'
import { PERMISSIONS } from '@/core/api/services/permissionService'
import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { ZoneCard } from '@/features/zones/components/ZoneCard'
import { StatusFilter } from '@/features/zones/List/ZoneStatusFilter'
import { Button } from '@/shared/components/atoms/Button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { AccountFilterDropdown } from '@/shared/components/molecules/AccountFilterDropdown/AccountFilterDropdown'
import { Card } from '@/shared/components/molecules/Card/Card'
import { PaginationControls } from '@/shared/components/molecules/PaginationControls/PaginationControls'
import { Search } from '@/shared/components/molecules/Search/Search'

interface ZonesTabProps {
	zones: PaginatedResponse<Zone> | undefined
	isLoading: boolean
	error: Error | null
	searchTerm: string
	statusFilter: StatusFilter
	setSearchTerm: (value: string) => void
	setStatusFilter: (value: StatusFilter) => void
	handleExportZones: () => void
	openDeleteModal: (zone: Zone) => void
	openRefreshModal: (zone: Zone) => void
	openEditModal: (zone: Zone) => void
	openSendCodeEmailModal: (zone: Zone) => void
	handleAddZone: () => void
	handleConnectZone: (zone: Zone) => void
	handleUpdateZone: (zoneId: number) => (data: UpdateZoneRequestDTO) => void
	isUpdating: boolean
	isTrialAccount?: boolean
	pagination: {
		current: number
		pageSize: number
		onChange: (page: number) => void
		onShowSizeChange: (current: number, size: number) => void
		showQuickJumper: boolean
		showSizeChanger: boolean
	}
	accountFilter: number | null
	setAccountFilter: (id: number | null) => void
	showAccountFilter: boolean
}

export const ZonesTab: React.FC<ZonesTabProps> = ({
	zones,
	isLoading,
	error,
	searchTerm,
	statusFilter,
	setStatusFilter,
	setSearchTerm,
	handleExportZones,
	openDeleteModal: _openDeleteModal,
	openRefreshModal,
	openEditModal,
	openSendCodeEmailModal: _openSendCodeEmailModal,
	handleAddZone,
	handleConnectZone,
	handleUpdateZone,
	isUpdating,
	isTrialAccount = false,
	pagination,
	accountFilter,
	setAccountFilter,
	showAccountFilter,
}) => {
	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const navigate = useNavigate()
	const { features } = usePermission()

	const { hasAnyPermission } = usePermission()
	const canCreateZone =
		hasAnyPermission([
			PERMISSIONS.MANAGE_CHILDREN_PLAYERS,
			PERMISSIONS.CREATE_USER_PLAYERS,
			PERMISSIONS.MANAGE_OWN_LEVEL_PLAYERS,
		]) && features.zone.create
	const canEditZone =
		hasAnyPermission([
			PERMISSIONS.MANAGE_CHILDREN_PLAYERS,
			PERMISSIONS.MANAGE_OWN_LEVEL_PLAYERS,
			PERMISSIONS.UPDATE_USER_PLAYERS,
		]) && features.zone.edit

	const handleStatusChange = (value: StatusFilter) => {
		setStatusFilter(value)
	}

	const handlePageSizeChange = (newSize: number) => {
		const currentFirstItemIndex = (pagination.current - 1) * pagination.pageSize
		const newPage = Math.floor(currentFirstItemIndex / newSize) + 1
		pagination.onShowSizeChange(newPage, newSize)
	}

	const filteredZones = (zones?.data || []).filter((zone) => {
		if (statusFilter === StatusFilter.ANY) return true

		const status = zone.state?.status
		if (!status) return false

		switch (statusFilter) {
			case StatusFilter.ONLINE:
				return (
					status === ZoneStateStatus.ONLINE ||
					status === ZoneStateStatus.WARNING
				)
			case StatusFilter.OFFLINE:
				return status === ZoneStateStatus.OFFLINE
			case StatusFilter.NOT_INSTALLED:
				return (
					status === ZoneStateStatus.NOT_INSTALLED ||
					status === ZoneStateStatus.NOT_REACTIVATED
				)
			default:
				return true
		}
	})

	const statusFilterContent = (
		<SelectContent>
			<div className="px-3 py-2 text-xs text-muted-foreground">
				{tCommon('entities.zone.status')}
			</div>
			<SelectItem value={StatusFilter.ANY}>
				{tCommon('entities.zone.filters.any')}
			</SelectItem>
			<SelectItem value={StatusFilter.ONLINE}>
				{tCommon('entities.zone.filters.online')}
			</SelectItem>
			<SelectItem value={StatusFilter.OFFLINE}>
				{tCommon('entities.zone.filters.offline')}
			</SelectItem>
			<SelectItem value={StatusFilter.NOT_INSTALLED}>
				{tCommon('entities.zone.filters.not_installed')}
			</SelectItem>
		</SelectContent>
	)

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between gap-4 w-full">
				<div className="flex items-center gap-3 flex-1 w-full">
					<Search
						value={searchTerm}
						onChange={setSearchTerm}
						placeholder={tCommon('entities.zone.search_placeholder')}
						className="w-full max-w-none lg:max-w-md"
					/>
					<div className="hidden lg:block">
						<Select value={statusFilter} onValueChange={handleStatusChange}>
							<SelectTrigger className="rounded-full h-8 text-sm font-medium">
								<SelectValue placeholder={tCommon('entities.zone.status')} />
							</SelectTrigger>
							{statusFilterContent}
						</Select>
					</div>
					{showAccountFilter && (
						<div className="hidden lg:block">
							<AccountFilterDropdown
								value={accountFilter}
								onChange={setAccountFilter}
							/>
						</div>
					)}
				</div>
				<div className="hidden md:flex items-center gap-2.5 justify-end">
					<Button
						variant="ghost"
						size="icon"
						onClick={handleExportZones}
						className="bg-secondary hover:bg-muted border border-border rounded-lg h-8 w-8"
						title={t('list.export')}
					>
						<Download className="h-4 w-4" />
					</Button>
					{canCreateZone && (
						<Button variant="action" size="action" onClick={handleAddZone}>
							{t('list.createZone')}
							<Plus className="h-4 w-4" />
						</Button>
					)}
				</div>
				<div className="md:hidden flex items-center gap-2.5 justify-end">
					{/* Filters button-icon only visible on mobile */}
					<Select value={statusFilter} onValueChange={handleStatusChange}>
						<SelectTrigger className="h-9 w-9 rounded-full border border-border bg-secondary/60 p-0 [&>svg]:hidden">
							<span className="relative flex items-center justify-center">
								<img src={filterIcon} alt="" className="h-10 w-10" />
								{statusFilter !== StatusFilter.ANY && (
									<span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-main-accent" />
								)}
							</span>
						</SelectTrigger>
						{statusFilterContent}
					</Select>
					{showAccountFilter && (
						<AccountFilterDropdown
							value={accountFilter}
							onChange={setAccountFilter}
						/>
					)}
				</div>
			</div>

			{isLoading ? (
				<div className="space-y-2">
					{Array.from({ length: 8 }).map((_, index) => (
						<Card
							key={index}
							className="overflow-hidden bg-card-dark border-border rounded-xl"
						>
							<div className="px-6 py-5">
								<div className="flex flex-col gap-4">
									<div className="flex items-center gap-2">
										<div className="h-6 bg-muted/10 rounded animate-pulse w-24" />
										<div className="h-6 bg-muted/10 rounded animate-pulse w-24" />
									</div>
									<div className="flex items-start justify-between gap-6">
										<div className="flex-1 flex flex-col gap-2">
											<div className="h-4 bg-muted/10 rounded animate-pulse w-32" />
											<div className="h-6 bg-muted/10 rounded animate-pulse w-full max-w-md" />
										</div>
										<div className="flex flex-col gap-2 min-w-[250px]">
											<div className="h-4 bg-muted/10 rounded animate-pulse w-20" />
											<div className="h-5 bg-muted/10 rounded animate-pulse w-40" />
										</div>
									</div>
								</div>
							</div>
						</Card>
					))}
				</div>
			) : error ? (
				<div className="p-6 border rounded-xl border-dashed bg-red-50">
					<div className="flex items-center gap-4">
						<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100">
							<XCircle className="h-6 w-6 text-red-500" />
						</div>
						<div className="flex-1">
							<h3 className="text-lg font-semibold mb-1 text-red-900">
								{t('list.errorLoading')}
							</h3>
							<p className="text-sm text-red-700">
								{t('list.errorDescription')}
							</p>
						</div>
					</div>
				</div>
			) : filteredZones.length === 0 ? (
				zones?.data && zones.data.length > 0 ? (
					<div className="flex flex-col items-center justify-center px-6 bg-card-dark rounded-2xl min-h-[calc(100vh-280px)]">
						<div className="text-center space-y-3 max-w-md">
							<h3 className="text-xl font-semibold">
								{tCommon('entities.zone.filters.empty_title')}
							</h3>
							<p className="text-sm text-muted-foreground">
								{tCommon('entities.zone.filters.empty_description')}
							</p>
						</div>
					</div>
				) : (
					<div className="flex flex-col items-center justify-center px-6 bg-card-dark rounded-2xl min-h-[calc(100vh-280px)]">
						<div className="text-center space-y-4 max-w-md">
							<div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-card/60 border border-border/60">
								<MapPin className="h-7 w-7 text-muted-foreground" />
							</div>
							<h3 className="text-2xl font-semibold">
								{t('list.empty.title')}
							</h3>
							<p className="text-sm text-muted-foreground">
								{t('list.empty.description')}
							</p>

							{canCreateZone && (
								<Button
									variant="default"
									className="rounded-full"
									onClick={handleAddZone}
								>
									<Plus className="mr-2 h-4 w-4" />
									{t('list.empty.action')}
								</Button>
							)}
						</div>
					</div>
				)
			) : (
				<>
					<div className="space-y-2">
						{filteredZones.map((zone) => (
							<ZoneCard
								key={zone.id}
								zone={zone}
								onClick={() => navigate(`/zones/${zone.id}`)}
								onUpdateZone={handleUpdateZone(zone.id)}
								isUpdating={isUpdating}
								onRefresh={openRefreshModal}
								onEdit={canEditZone ? openEditModal : undefined}
								onConnectZone={handleConnectZone}
								isTrialAccount={isTrialAccount}
							/>
						))}
					</div>

					<PaginationControls
						currentPage={pagination.current}
						totalPages={zones?.meta.pagination.total_pages ?? 1}
						onPageChange={pagination.onChange}
						pageSize={pagination.pageSize}
						onPageSizeChange={handlePageSizeChange}
						className="md:pt-6"
					/>
				</>
			)}

			{canCreateZone && (
				<button
					type="button"
					onClick={handleAddZone}
					className="md:hidden fixed bottom-[110px] right-4 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-transparent shadow-lg transition hover:opacity-90"
					aria-label={t('list.createZone')}
				>
					<span
						className="h-14 w-14 bg-no-repeat"
						style={{
							backgroundImage: `url(${addIcon})`,
							backgroundSize: '157px 209px',
							backgroundPosition: '-88px -48px',
						}}
					/>
				</button>
			)}
		</div>
	)
}
